import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ParticleBackground from "./components/ParticleBackground";
import ResetPassword from "./components/ResetPassword";
import ContentCard from "./components/ContentCard";
import NewsPage from "./pages/NewsPage";
import CoursesPage from "./pages/CoursesPage";
import SearchPage from "./pages/SearchPage";
import ContentDetail from "./pages/ContentDetail";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminChangePassword from "./pages/admin/AdminChangePassword";
import AdminContentManager from "./pages/admin/AdminContentManager.jsx";
import AdminContentArchive from "./pages/admin/AdminContentArchive.jsx";
import AdminForgotPassword from "./pages/admin/AdminForgotPassword";
import AdminResetPassword from "./pages/admin/AdminResetPassword";
import ContactPage from "./pages/ContactPage";
import "./styles/main.css";
import "./styles/admin.css";
import ContactMessagesPage from "./pages/admin/ContactMessagesPage";
import AboutPage from "./pages/AboutPage";
import { trackSiteVisit } from "./api/analyticsApi";



function PublicLayout({ children }) {
  const location = useLocation();

  useEffect(() => {
    trackSiteVisit({
      path: `${location.pathname}${location.search || ""}`,
      referrer: document.referrer || "",
    });
  }, [location.pathname, location.search]);

  return (
    <div className="app">
      <ParticleBackground />

      <div className="page-wrapper">
        <Header />
        {children}
        <Footer />
      </div>
    </div>
  );
}

function AuthLayout({ children }) {
  return (
    <div className="app">
      <ParticleBackground />
      {children}
    </div>
  );
}

function HomePage() {
  const [latestNews, setLatestNews] = useState([]);
  const [latestCourses, setLatestCourses] = useState([]);
  const [homeLoading, setHomeLoading] = useState(true);

  const newsSectionRef = useRef(null);
  const coursesSectionRef = useRef(null);

  const [visibleSections, setVisibleSections] = useState({
    news: false,
    courses: false,
  });

  useEffect(() => {
    const fetchHomeContents = async () => {
      try {
        setHomeLoading(true);

        const [newsResponse, coursesResponse] = await Promise.all([
          fetch("/api/content?type=news"),
          fetch("/api/content?type=course"),
        ]);

        const newsData = await newsResponse.json();
        const coursesData = await coursesResponse.json();

        if (newsResponse.ok) {
          setLatestNews((newsData.contents || []).slice(0, 3));
        }

        if (coursesResponse.ok) {
          setLatestCourses((coursesData.contents || []).slice(0, 3));
        }
      } catch (error) {
        console.error("Home contents fetch error:", error);
      } finally {
        setHomeLoading(false);
      }
    };

    fetchHomeContents();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionName = entry.target.dataset.section;

          if (entry.isIntersecting && sectionName) {
            setVisibleSections((prev) => ({
              ...prev,
              [sectionName]: true,
            }));

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
      }
    );

    if (newsSectionRef.current) {
      observer.observe(newsSectionRef.current);
    }

    if (coursesSectionRef.current) {
      observer.observe(coursesSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <main>
      <section className="hero-section glass-card">
        <div className="hero-text">
          <span className="hero-badge">پلتفرم آموزشی و اخبار فناوری</span>

          <h1 className="hero-animated-text">
            یادگیری هوشمند، اخبار به‌روز
          </h1>

          <p>
            جدیدترین اخبار دنیای تکنولوژی، آموزش‌های کاربردی، تحلیل هوش مصنوعی،
            امنیت سایبری و مسیرهای حرفه‌ای یادگیری را در یک پلتفرم مدرن تجربه
            کنید.
          </p>

          <div className="hero-actions">
            <Link to="/courses" className="btn btn-primary">
              شروع یادگیری
            </Link>

            <Link to="/news" className="btn btn-secondary">
              مشاهده اخبار
            </Link>
          </div>
        </div>

        <div className="hero-panel glass-card">
          <div className="mini-stat">
            <strong>+250</strong>
            <span>دوره آموزشی</span>
          </div>

          <div className="mini-stat">
            <strong>+18K</strong>
            <span>کاربر فعال</span>
          </div>

          <div className="mini-stat">
            <strong>+900</strong>
            <span>مطلب و خبر</span>
          </div>

          <div className="mini-stat">
            <strong>96%</strong>
            <span>رضایت کاربران</span>
          </div>
        </div>
      </section>

      <section
        ref={newsSectionRef}
        data-section="news"
        className={`section-block home-showcase-section news-showcase reveal-section reveal-from-right ${
          visibleSections.news ? "show" : ""
        }`}
      >
        <div className="section-title">
          <h2>اخبار تکنولوژی</h2>
          <p>آخرین خبرها از هوش مصنوعی، امنیت، برنامه‌نویسی و فناوری</p>
        </div>

        <div className="content-grid">
          {homeLoading ? (
            <p className="empty-home-content">در حال دریافت اخبار...</p>
          ) : latestNews.length > 0 ? (
            latestNews.map((item, index) => (
              <ContentCard key={item._id || item.id} item={item} priority={index < 3} />
            ))
          ) : (
            <p className="empty-home-content">هنوز خبری منتشر نشده است.</p>
          )}
        </div>

        <div className="section-more">
          <Link to="/news" className="primary-link">
            مشاهده همه اخبار
          </Link>
        </div>
      </section>

      <section
        ref={coursesSectionRef}
        data-section="courses"
        className={`section-block home-showcase-section courses-showcase reveal-section reveal-from-left ${
          visibleSections.courses ? "show" : ""
        }`}
      >
        <div className="section-title">
          <h2>دوره‌های آموزشی</h2>
          <p>برای شروع یا رشد حرفه‌ای، از این مسیرها شروع کن</p>
        </div>

        <div className="content-grid">
          {homeLoading ? (
            <p className="empty-home-content">در حال دریافت دوره‌ها...</p>
          ) : latestCourses.length > 0 ? (
            latestCourses.map((item, index) => (
              <ContentCard key={item._id || item.id} item={item} priority={index < 3} />
            ))
          ) : (
            <p className="empty-home-content">هنوز دوره‌ای منتشر نشده است.</p>
          )}
        </div>

        <div className="section-more">
          <Link to="/courses" className="primary-link">
            مشاهده همه دوره‌ها
          </Link>
        </div>
        
      </section>
    </main>
  );
}

function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicLayout>
            <HomePage />
          </PublicLayout>
        }
      />

      <Route
        path="/news"
        element={
          <PublicLayout>
            <NewsPage />
          </PublicLayout>
        }
      />

      <Route
        path="/news/:slug"
        element={
          <PublicLayout>
            <ContentDetail type="news" />
          </PublicLayout>
        }
      />

      <Route
        path="/courses"
        element={
          <PublicLayout>
            <CoursesPage />
          </PublicLayout>
        }
      />

      <Route
        path="/courses/:slug"
        element={
          <PublicLayout>
            <ContentDetail type="course" />
          </PublicLayout>
        }
      />

      <Route
        path="/search"
        element={
          <PublicLayout>
            <SearchPage />
          </PublicLayout>
        }
      />

      <Route
        path="/reset-password/:token"
        element={
          <AuthLayout>
            <ResetPassword />
          </AuthLayout>
        }
      />

      <Route
        path="/admin/forgot-password"
        element={
          <AuthLayout>
            <AdminForgotPassword />
          </AuthLayout>
        }
      />

      <Route
        path="/admin/reset-password/:token"
        element={
          <AuthLayout>
            <AdminResetPassword />
          </AuthLayout>
        }
      />
      <Route
       path="/about"
       element={
      <PublicLayout>
      <AboutPage />
      </PublicLayout>
      }
      />

      <Route
      path="/contact"
      element={
      <PublicLayout>
      <ContactPage />
      </PublicLayout>
       }
/>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/content" element={<AdminContentManager />} />
      <Route path="/admin/content/archive" element={<AdminContentArchive />} />
      <Route path="/admin/change-password" element={<AdminChangePassword />} />
      <Route path="/admin/contact-messages" element={<ContactMessagesPage />}
      
/>
    </Routes>
  );
}

export default App;